import { EFinancingCompany } from '@if/core/types'
import { Button, Menu, MenuItem } from '@mui/material'
import { useLogout, useMockLogin } from '@src/data/api/user/user-api'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'

import { useOptionalUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { MouseEvent, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDevTools } from '../../../data/types'
import ListMockedLoginDialog from '../../Home/components/ListMockedLoginDialog'

const UserMenu = (): JSX.Element => {
  const { t } = useTranslation()
  const menuStyle = { textDecoration: 'none' }
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isLoginMockDialogOpen, setIsLoginMockDialogOpen] = useState<boolean>(false)
  const navigate = useNavigate()
  const [mockLogin] = useMockLogin()
  const logout = useLogout()
  const featureFlags = useFeatureFlags()
  const open = Boolean(anchorEl)

  const user = useOptionalUser()

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const onMockedLoginSelected = (username: string, companyIds: EFinancingCompany[]) => {
    mockLogin({ username, password: 'ok', companyIds }).catch(reportErrorToConsole)
  }

  return (
    <>
      <ListMockedLoginDialog
        onMockedLoginSelected={(username, companyIds) => {
          onMockedLoginSelected(username, companyIds)
          setIsLoginMockDialogOpen(false)
        }}
        open={isLoginMockDialogOpen}
        onClose={() => setIsLoginMockDialogOpen(false)}
      />
      <>
        <Button
          id="btnAdmin"
          aria-controls={open ? 'admin-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          Admin
        </Button>
        <Menu id="admin-menu" anchorEl={anchorEl} open={open && anchorEl?.id === 'btnAdmin'} onClose={handleClose}>
          <MenuItem
            style={menuStyle}
            onClick={() => {
              window.open(import.meta.env.VITE_MERCHANT_APP_URL, '_blank')
            }}
          >
            {t('common.merchantManagement')}
          </MenuItem>
          {featureFlags.useInstantMessage && (
            <MenuItem style={menuStyle} onClick={() => navigate('/chat-messages')}>
              {t('common.templateMessageManagement')}
            </MenuItem>
          )}
          <MenuItem style={menuStyle} onClick={() => navigate('/featureSwitch')}>
            {t('featureSwitch.featureSwitch')}
          </MenuItem>
          <MenuItem style={menuStyle} onClick={() => navigate('/banishments')}>
            {t('banishments.title')}
          </MenuItem>
          {useDevTools && (
            <MenuItem style={menuStyle} onClick={() => navigate('/manageTestCases')}>
              {t('common.testCaseManagement')}
            </MenuItem>
          )}
        </Menu>
      </>

      <>
        <Button
          id="btnUser"
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          {user?.name}
        </Button>
        <Menu id="user-menu" anchorEl={anchorEl} open={open && anchorEl?.id === 'btnUser'} onClose={handleClose}>
          {useDevTools && (
            <MenuItem
              style={menuStyle}
              onClick={() => {
                handleClose()
                setIsLoginMockDialogOpen(true)
              }}
            >
              {t('common.impersonateTestUser')}
            </MenuItem>
          )}
          <MenuItem style={menuStyle} onClick={logout}>
            {t('menus.user.logout')}
          </MenuItem>
        </Menu>
      </>
    </>
  )
}

export default UserMenu
